import {FC, ReactElement, Suspense} from 'react'
import {Navigate, Outlet} from 'react-router-dom'
import {I18nProvider} from '../_payairo/i18n/i18nProvider'
import {LayoutProvider, LayoutSplashScreen} from '../_payairo/layout/core'
import {MasterInit} from '../_payairo/layout/MasterInit'
import {AuthInit, useAuth} from './modules/auth'
import {ThemeModeProvider} from '../_payairo/partials'
import {ToastProvider} from './hooks/useToast'
import {ToastContainer} from './components/ToastNotification'
import {getDashboardPath, normalizeRoleKey} from './utils/helper'

export type ProtectedRouteProps = {
  children: ReactElement
  /** If set, only users whose role or user_type matches one of these (case-insensitive) may access */
  allowedRoles?: string[]
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({children, allowedRoles}) => {
  const {currentUser, auth} = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  if (!allowedRoles?.length) {
    return children
  }

  const fromUser = [role, userType].filter(Boolean) as string[]
  const allowed = allowedRoles.map((r) => normalizeRoleKey(r))
  const ok = fromUser.some((r) => allowed.includes(normalizeRoleKey(r)))

  if (!ok) {
    return <Navigate to={dashboardPath} replace />
  }

  return children
}

const App = () => {
  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      <I18nProvider>
        <LayoutProvider>
          <ThemeModeProvider>
            <ToastProvider>
              <AuthInit>
                <Outlet />
                <MasterInit />
                <ToastContainer />
              </AuthInit>
            </ToastProvider>
          </ThemeModeProvider>
        </LayoutProvider>
      </I18nProvider>
    </Suspense>
  )
}

export {App}
